/**
 * 🎒 Battle Bag Button + Modal (Mar 5, 2026)
 *
 * Renders a 🎒 button in the combat action row. Tapping opens a modal of the
 * user's healing consumables (HP potions, MP ethers, Elixir, Warm Broth,
 * Phoenix Dew — from consumables_service). Using an item costs the player's
 * turn — resolves server-side.
 *
 * Props:
 *   - disabled: boolean (turn ownership + submitting guard from parent)
 *   - onUse: async (consumable_id) => void  — parent handles the POST
 *   - label: string (optional, defaults to "Bag")
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BattleBagButton({ disabled = false, onUse, label = "Bag" }) {
  const [open, setOpen] = useState(false);
  const [bag, setBag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [using, setUsing] = useState(null);

  const loadBag = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/consumables/bag`);
      setBag(r.data || null);
    } catch (e) {
      toast.error("Couldn't load your Battle Bag.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadBag();
  }, [open]);

  const owned = (bag?.items || []).filter((it) => (it.count || 0) > 0);

  const handleUse = async (cid) => {
    if (using) return;
    setUsing(cid);
    try {
      await onUse(cid);
      setOpen(false);
    } catch (e) {
      // parent surfaces the error toast
    } finally {
      setUsing(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        data-testid="battle-bag-btn"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-950/60 hover:bg-amber-900/70 border border-amber-700/70 text-amber-100 text-sm font-black uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        title="Open Battle Bag"
      >
        <span className="text-lg leading-none">🎒</span>
        <span className="hidden sm:inline">{label}</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-4"
          data-testid="battle-bag-modal"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border-2 border-amber-600/60 bg-gradient-to-br from-amber-950/40 via-black to-amber-950/30 p-5 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400">
                  Battle Bag
                </div>
                <div
                  className="text-2xl font-black text-amber-200"
                  style={{ fontFamily: "Anton, sans-serif" }}
                >
                  🎒 CONSUMABLES
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="battle-bag-close"
                className="text-amber-200 hover:text-amber-100 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="text-[10px] text-amber-100/70 italic mb-3">
              Using an item costs your turn — same as swinging.
            </div>

            {loading && (
              <div className="text-center text-amber-200 py-8 animate-pulse">
                Loading bag…
              </div>
            )}

            {!loading && owned.length === 0 && (
              <div className="rounded-lg bg-black/60 border border-neutral-800 p-6 text-center">
                <div className="text-4xl mb-2 opacity-40">🎒</div>
                <div className="text-sm text-neutral-400">Your bag is empty.</div>
                <div className="text-[11px] text-neutral-500 mt-1">
                  Spin the Wheel or clear Wilds encounters to stock up.
                </div>
              </div>
            )}

            {!loading && owned.length > 0 && (
              <div className="space-y-2">
                {owned.map((it) => {
                  const tint = it.tier_meta?.color_hex || "#a3a3a3";
                  const isUsing = using === it.consumable_id;
                  return (
                    <button
                      key={it.consumable_id}
                      type="button"
                      onClick={() => handleUse(it.consumable_id)}
                      disabled={using != null}
                      data-testid={`battle-bag-item-${it.consumable_id}`}
                      className="w-full text-left rounded-lg border bg-black/60 hover:bg-black/40 px-3 py-2.5 disabled:opacity-40 transition-all"
                      style={{ borderColor: tint }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-2xl shrink-0"
                          style={{
                            backgroundColor: `${tint}22`,
                            border: `1px solid ${tint}55`,
                          }}
                        >
                          {it.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className="font-black text-sm truncate"
                              style={{ color: tint }}
                            >
                              {it.name}
                            </span>
                            <span className="text-[11px] font-mono text-amber-200 shrink-0">
                              ×{it.count}
                            </span>
                          </div>
                          <div className="text-[10px] text-neutral-400 leading-snug mt-0.5">
                            {it.description}
                          </div>
                        </div>
                      </div>
                      {isUsing && (
                        <div className="mt-1.5 text-[10px] text-amber-300 italic">
                          Consuming…
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
